import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  noindex?: boolean;
  nofollow?: boolean;
  canonical?: string;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'Arpan PhysioCure™ - Premier Physiotherapy Clinic in Indore',
  description = 'Leading physiotherapy clinic in Indore offering sports physiotherapy, orthopedic rehabilitation, neurological care, and corporate wellness programs. Expert treatment by certified physiotherapists.',
  keywords = 'physiotherapy Indore, sports physiotherapy, orthopedic rehabilitation, neurological physiotherapy, corporate wellness, Dr. Pragati Nayak, Dr. Deep Maheshwari',
  image = 'https://arpanphysiocure.com/og-image.jpg',
  url = 'https://arpanphysiocure.com',
  type = 'website',
  author = 'Arpan PhysioCure™',
  publishedTime,
  modifiedTime,
  section,
  tags = [],
  noindex = false,
  nofollow = false,
  canonical,
}) => {
  const fullTitle = title.includes('Arpan PhysioCure™') 
    ? title 
    : `${title} | Arpan PhysioCure™`;

  const robotsContent = [
    noindex ? 'noindex' : 'index',
    nofollow ? 'nofollow' : 'follow',
  ].join(', ');

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content={robotsContent} />
      
      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Arpan PhysioCure™" />
      <meta property="og:locale" content="en_IN" />
      
      {/* Article specific meta tags */}
      {type === 'article' && (
        <>
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {author && <meta property="article:author" content={author} />}
          {section && <meta property="article:section" content={section} />}
          {tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@arpanphysiocure" />
      <meta name="twitter:creator" content="@arpanphysiocure" />
      
      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#0ea5e9" />
      <meta name="msapplication-TileColor" content="#0ea5e9" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Arpan PhysioCure™" />
      
      {/* Preconnect to external domains */}
      <link rel="preconnect" href="https://images.pexels.com" />
      <link rel="dns-prefetch" href="https://images.pexels.com" />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalBusiness",
          "name": "Arpan PhysioCure™",
          "description": description,
          "url": url,
          "telephone": "+91-98765-43210",
          "email": "info@arpanphysiocure.com",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "123 Medical Plaza, Vijay Nagar",
            "addressLocality": "Indore",
            "addressRegion": "Madhya Pradesh",
            "postalCode": "452010",
            "addressCountry": "IN"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "22.7196",
            "longitude": "75.8577"
          },
          "openingHours": [
            "Mo-Sa 08:00-20:00",
            "Su 09:00-14:00"
          ],
          "medicalSpecialty": [
            "Sports Physiotherapy",
            "Orthopedic Rehabilitation",
            "Neurological Physiotherapy",
            "Pediatric Care",
            "Geriatric Care"
          ],
          "priceRange": "$$",
          "image": image,
          "sameAs": [
            "https://www.facebook.com/arpanphysiocure",
            "https://www.instagram.com/arpan.physiocure",
            "https://twitter.com/arpanphysiocure"
          ]
        })}
      </script>
    </Helmet>
  );
};

export default SEOHead;